import { Html, OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import React, { useEffect, useMemo, useRef } from 'react';
import { Vector3, TOUCH, Plane } from 'three';
import TWEEN from 'tween';
import { useShallow } from 'zustand/react/shallow';
import HolesLoader from '../../loaders/HolesLoader.js';
import LoadModel from '../../loaders/LoadModel.js';
import CameraControl from '../../clickTools/CameraControl.js';
import MeasureLines from '../../clickTools/MeasureLines.js';
import SceneGrid from '../../clickTools/SceneGrid.js';
import { useClickStore, useInforStore } from '../../store/clickStore.js';
import { useSectionStore } from '../../store/sectionStore';
import { getLevelLabels, getOriginENR } from '../../config/siteConfig';
import SectionClipper from '../SceneSlicer/SectionClipper';
import SectionGizmo from '../SceneSlicer/SectionGizmo';
import SectionLookRig from '../SceneSlicer/SectionLookRig';
import PlanLookRig from '../SceneSlicer/PlanLookRig';
import StringLines from '../../loaders/StringLines.js';
import { collectHoleLayerKeys, isHoleObject, sceneToMine } from '../../Utils/sectionMath';
import { snapScenePoint } from '../../Utils/sceneSnap';
import { useStringLayerStore } from '../../store/stringLayerStore';
import BlockModels from '../../loaders/BlockModels.js';
import AssayLoader from '../../loaders/AssayLoaderTM.js';
import HoleRender from '../../loaders/holeTools/HoleRender.js';
import CachedHoleRender from '../../loaders/holeTools/CachedHoleRender.js';
import DatabaseHoleRender from '../../loaders/holeTools/DatabaseHoleRender.js';
import HighlightedObject from '../../clickTools/HighlightedObject.js';
import { resolveLayerKeyFromObject } from '../../Utils/pickLayerKey.js';
import { pickedObjectLabel } from '../../Utils/objectIdMatch.js';
import {
  defaultGroupRaycast,
  firstPickableHit,
  isPickableHit,
  skipHiddenSubtreeRaycast,
} from '../../Utils/pickVisibleHit.js';
import SceneLights from './SceneLights.js';
import { resolvedHoleIntervalMode, useHoleDisplayStore } from '../../store/holeDisplayStore';

const noop = () => {};

const SceneComponent = ({
  modelList = [],
  layerVisibility,
  cameraPosition,
  lookAt,
  animateCamera,
  onThreeReady,
  onLayerLoadComplete = noop,
  onLayerLoadFailed = noop,
  showGrid = false,
  sceneLookId,
}) => {
  const camera = useThree((s) => s.camera)
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const raycaster = useThree((s) => s.raycaster)
  const setEvents = useThree((s) => s.setEvents)
  const invalidate = useThree((s) => s.invalidate)
  const controlRef = useRef()

  const {setInfo} =    useInforStore(useShallow((state)=>({
      setInfo:state.setInfo
  })))

  const {setClickParams, setClickType} = useClickStore(useShallow((state)=>({        
      setClickParams:state.setClickParams,
      setClickType:state.setClickType,
  })))
  const intervalModeByLayer = useHoleDisplayStore((s) => s.intervalModeByLayer);
  const desurveyEnabled = useHoleDisplayStore((s) => s.desurveyEnabled);
  const digitizing = useStringLayerStore((s) => s.digitizing);
  const placeMode = useSectionStore((s) => s.placeMode);
  const clipHoles = useSectionStore((s) => s.clipHoles);
  const holeLayerKeys = useMemo(() => collectHoleLayerKeys(modelList), [modelList]);
  const skipClipFor = useMemo(
    () => (object) => !clipHoles && isHoleObject(object, holeLayerKeys),
    [clipHoles, holeLayerKeys]
  );
  const clickTimesRef = useRef([])
  const clickPositionsRef = useRef([])
  const TRIPLE_CLICK_TIMEOUT = 500
  const MAX_CLICK_DISTANCE = 50
  useEffect(()=>{
    onThreeReady({ camera, gl, scene, raycaster , controlRef })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(() => {
    setEvents({
      filter: (hits, state) =>
        hits.filter((hit) => isPickableHit(hit, { gl: state.gl, layerVisibility, skipClipFor })),
    });
  }, [setEvents, layerVisibility, skipClipFor]);

  const manage_click=(e)=>{
      const hit = firstPickableHit(e, { gl, layerVisibility, skipClipFor });
      let rawPoint = hit?.point;
      let object = hit?.object;
      if (!rawPoint && e.ray && controlRef.current) {
        const planeHit = new Vector3();
        const plane = new Plane(new Vector3(0, 1, 0), -controlRef.current.target.y);
        if (e.ray.intersectPlane(plane, planeHit)) {
          rawPoint = planeHit;
          object = object || { name: 'plan' };
        }
      }
      if (!rawPoint) {
        e.stopPropagation();
        return;
      }
      const snap = snapScenePoint(rawPoint, {
        camera,
        enabled: useClickStore.getState().snapEnabled,
      });
      const point = snap.point;
      useClickStore.getState().setLastSnap(snap.kind === 'none' ? null : snap);
      if (useStringLayerStore.getState().digitizing) {
        useStringLayerStore.getState().addDraftPoint(snap.mine);
        e.stopPropagation();
        return;
      }
      const placeMode = useSectionStore.getState().placeMode;
      if (placeMode === 'object') {
        useSectionStore.getState().setClipParentFromObject(object);
        e.stopPropagation();
        return;
      }
      if (placeMode && placeMode !== 'idle') {
        useSectionStore.getState().addPlacePoint(point);
        e.stopPropagation();
        return;
      }
      const instanceId =
        hit.instanceId ??
        hit.index ??
        e.instanceId ??
        e.index ??
        e.intersections?.[0]?.instanceId ??
        e.intersections?.[0]?.index;
      const layerKey = resolveLayerKeyFromObject(object, layerVisibility);

      const now = Date.now();
      const clickTimes = clickTimesRef.current;
      const clickPositions = clickPositionsRef.current;
      clickTimes.push(now);
      clickPositions.push(point);
      while (clickTimes.length && now - clickTimes[0] > TRIPLE_CLICK_TIMEOUT) {
        clickTimes.shift();
        clickPositions.shift();
      }
      if (clickTimes.length >= 3) {
        const recent = clickPositions.slice(-3);
        const dist = (a, b) =>
          Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.z - b.z);
        if (dist(recent[0], recent[1]) < MAX_CLICK_DISTANCE && dist(recent[1], recent[2]) < MAX_CLICK_DISTANCE) {
          setClickType('zoom');
          setClickParams(point, object, instanceId, layerKey);
          clickTimesRef.current = [];
          clickPositionsRef.current = [];
          e.stopPropagation();
          return;
        }
      }

      setClickParams(point, object, instanceId, layerKey)
      const mine = snap.mine || sceneToMine(point.x, point.y, point.z);
      setInfo({
        name: snap.label
          ? `${pickedObjectLabel(object, instanceId, layerKey)} · ${snap.label}`
          : pickedObjectLabel(object, instanceId, layerKey),
        east: mine.east,
        north: mine.north,
        rl: mine.rl,
      });
      e.stopPropagation()        
  }

  
  useEffect(() => {
    if (animateCamera) {
      const endPosition = new Vector3(...cameraPosition);
      const endLookAt = new Vector3(...lookAt);

      new TWEEN.Tween(camera.position)
        .to(endPosition, 5000)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();

      new TWEEN.Tween(controlRef.current.target)
        .to(endLookAt, 5000)
        .easing(TWEEN.Easing.Cubic.Out)
        .start();
      invalidate();

    } else if (cameraPosition && lookAt) {
      camera.position.set(...cameraPosition);
      if (controlRef.current) {
        controlRef.current.target.set(...lookAt);
        controlRef.current.update();
      }
      invalidate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraPosition, lookAt, animateCamera]);

  useFrame(() => {
    TWEEN.update();
    if (TWEEN.getAll().length > 0) invalidate();
  });

  const activeLayers = useMemo(() => {
    const rows = [];
    (modelList || []).forEach((group) => {
      (group.models || []).forEach((model) => {
        const modelState = layerVisibility[model.name];
        if (modelState?.visible || modelState?.loadStatus === 'loading') {
          rows.push({ model, modelState });
        }
      });
    });
    return rows;
  }, [modelList, layerVisibility]);
  const clipRev = useMemo(() => activeLayers.map((row) => row.model.name).join('|'), [activeLayers]);

  return (
    <>
      <SceneLights lookId={sceneLookId} />
      <OrbitControls
        makeDefault
        ref={controlRef}
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.72}
        zoomSpeed={0.9}
        panSpeed={0.65}
        touches={{ ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN }}
      />
      <SceneGrid visible={showGrid} />
      <SectionClipper modelList={modelList} clipRev={clipRev} />
      <SectionGizmo />
      <SectionLookRig controlRef={controlRef} />
      <PlanLookRig controlRef={controlRef} />
      <StringLines />
      <MeasureLines />
      {(digitizing || (placeMode && placeMode !== 'idle')) ? (
        <PickPlane controlRef={controlRef} onClick={(e) => manage_click(e)} />
      ) : null}
      <group onClick={(e)=>{ manage_click(e)} } >
      {activeLayers.map(({ model, modelState }) => {
          const isVisible = !!modelState?.visible;
          const opacity = modelState?.opacity ?? 1;
          const modelType = String(model.type || '').toLowerCase();
          const layerKey = model.name;
          const layerNode =
              (modelType === 'holes')?
              <HolesLoader name={layerKey} url={model.url} dataset={model.dataset} holetype={model.holetype} visible={isVisible} onLoadComplete={onLayerLoadComplete} onLoadFailed={onLayerLoadFailed} />
              :
              (modelType === 'blockmodel')?
              <BlockModels url={model.url} name={layerKey} dx={model.dx} dy={model.dy} dz={model.dz} size={model.size} grade={model.grade} minGrade={model.minGrade} maxGrade={model.maxGrade} colorBy={model.colorBy} sx={model.sx} sy={model.sy} sz={model.sz} bmvisible={isVisible} onLoadComplete={onLayerLoadComplete} onLoadFailed={onLayerLoadFailed} />
              :
              (modelType === 'assays')?
              <AssayLoader name={layerKey} url={model.url} visible={isVisible} onLoadComplete={onLayerLoadComplete} onLoadFailed={onLayerLoadFailed} />
              :
              (modelType === 'holesassays')?
              <HoleRender name={layerKey} hole_url={model.hole_url} assay_url={model.assay_url}  visible={isVisible} onLoadComplete={onLayerLoadComplete} onLoadFailed={onLayerLoadFailed} />
              :
              (modelType === 'cachedholes')?
              <CachedHoleRender name={layerKey} hole_url={model.hole_url} assay_url={model.assay_url} dataset={model.dataset} holetype={model.holetype} visible={isVisible} onLoadComplete={onLayerLoadComplete} onLoadFailed={onLayerLoadFailed} />
              :
              (modelType === 'databaseholes')?
              <DatabaseHoleRender
                name={layerKey}
                dataset={model.dataset}
                holetype={model.holetype}
                interval_mode={resolvedHoleIntervalMode(model, intervalModeByLayer)}
                isplan={model.isplan ?? false}
                coll_url={model.coll_url}
                assay_url={model.assay_url}
                geology_url={model.geology_url}
                max_rows={model.max_rows}
                collarQuery={model.collarQuery || null}
                desurvey={desurveyEnabled}
                visible={isVisible}
                onLoadComplete={onLayerLoadComplete}
                onLoadFailed={onLayerLoadFailed}
              />
              :
              (model.url ? (
                <LoadModel
                  name={layerKey}
                  url={model.url}
                  visible={isVisible}
                  opacity={opacity}
                  shouldBloom={model.shouldBloom ? model.shouldBloom : false}
                  onLoadComplete={onLayerLoadComplete}
                  onLoadFailed={onLayerLoadFailed}
                />
              ) : null);
          if (!layerNode) return null;
          return (
            <group
              key={
                model.url
                  ? `${layerKey}::${model.url}::${model.mtime || ''}::${model.size || ''}::${modelState?.cacheRev || ''}`
                  : layerKey
              }
              visible={isVisible}
              raycast={isVisible ? defaultGroupRaycast : skipHiddenSubtreeRaycast}
            >
              {layerNode}
            </group>
          );
      })}
      </group>
      <HighlightedObject />
      <CameraControl controls={controlRef}/>
      <Levels/>  
    </>
  );
};

function PickPlane({ controlRef, onClick }) {
  const meshRef = useRef();
  useFrame(() => {
    const y = controlRef?.current?.target?.y;
    if (meshRef.current && Number.isFinite(y)) meshRef.current.position.y = y;
  });
  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      userData={{ mdvPickPlane: true, mdvSkipClip: true }}
      onClick={onClick}
    >
      <planeGeometry args={[80000, 80000]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

function Levels() {
  const origin = getOriginENR();
  const { showLabels } = useClickStore();
  const { offsetEast, offsetNorth, levels } = getLevelLabels();

  if (!showLabels || !levels?.length) return null;

  return (
      <>
         {levels.map((a) => (
                  <Html 
                      key={`${a[0]}-${a[1]}`}
                      position={[offsetEast, a[1] - origin[2], -offsetNorth]}
                      center
                      transform
                      sprite
                      distanceFactor={500}
                      >
                      <div className="levels">
                          {a[0] + '-' + (origin[2]-a[1])+'m (' + a[1]+'RL)'} 
                      </div>
                      
                  </Html>
          ))}
      </>
  )
}

export default React.memo(SceneComponent);
